// combatpro/views/AbilityScoresView.js
//
// Stage 33: view-ability-scores shell. Stage 67 (Ability Scores View Foundation)
// migrates the legacy per-monster Ability Scores card
// (battletracker.html ability-scores-block, 6237-6322) for the ACTIVE monster.
//
// The legacy built one ability-scores-block-${i} per monster inside
// createMonsterPanel and reparented the active one into #vi-ability-scores-mount
// via vi_mountAbilityScores (10094). React renders the active monster's card
// directly from state.currentCombatantIndex (the MoraleView pattern) - no
// reparenting, no getElementById.
//
// Faithful migration (master spec preserved verbatim):
//   - the section header and the three static helper cards (Database Monster /
//     Custom & Core System / How to Use), 6238-6269.
//   - the six ability boxes (STR/DEX/CON/INT/WIS/CHA), 6270-6320: an editable score
//     input bound to state, the derived modifier "(+N)"/"(-N)", and the stored save
//     display "[Save: +N]".
//
// Ownership: state is owned by CombatContext. The score write goes through the
// Stage 67 updateAbilityScore handler (the nested abilityScores write
// updateMonsterField cannot express). The modifier is computed by the existing
// initiativeEngine.getAbilityModifier (the byte-identical migration of the legacy
// getAbilityModifier, 7978) - not duplicated here; the component only formats the
// sign, exactly as the legacy display did. Saves are read directly from
// monster.saves[ability] and shown signed - never recomputed, no proficiency
// inferred (the legacy displayed the stored value only). The legacy DEX ->
// updateInitiativeModifier DOM poke is intentionally not recreated: the Ability
// Scores page updates monster state only, and the Initiative view re-derives from
// state when rendered. No DOM manipulation, no getElementById, no new runtime, no
// CombatContext ownership change. No optional chaining. No em dash.

import React, { useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { getAbilityModifier } from '../initiative/initiativeEngine';

const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

// Signed string the legacy used for both the modifier and the save display
// (modifier >= 0 ? "+" + modifier : "" + modifier).
function signed(n) {
  return (n >= 0 ? '+' + n : '' + n);
}

function AbilityScoresView() {
  const { state, updateAbilityScore, rollAbilityCheck, rollSave } = useContext(CombatContext);
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];

  if (!monster) {
    return (
      <div className="vi-view" id="view-ability-scores">
        <div id="vi-ability-scores-mount"></div>
      </div>
    );
  }

  const abilityScores = monster.abilityScores || {};
  const saves = monster.saves || {};

  return (
    <div className="vi-view" id="view-ability-scores">
      <div id="vi-ability-scores-mount">
        <div id={'ability-scores-block-' + index}>
          <div className="vi-section-header vi-ability-scores-header">
            <h3>⚔️ Ability Scores</h3>
          </div>
          <div className="vi-ability-helper-grid">
            <div className="vi-ability-card">
              <div className="vi-ability-title">Database Monster</div>
              <p>Scores are automatically loaded from the official stat block.</p>
              <p>You can edit any value to adjust difficulty or behaviour.</p>
            </div>
            <div className="vi-ability-card">
              <div className="vi-ability-title">Custom & Core System</div>
              <p><strong>Custom monster:</strong><br />
              You must enter all six scores manually.</p>
              <p><strong>These values control:</strong></p>
              <div className="vi-howto-steps">
                <p>Attack rolls (STR / DEX)</p>
                <p>Saving throws</p>
                <p>Initiative (DEX)</p>
                <p>Overall combat effectiveness</p>
              </div>
            </div>
            <div className="vi-ability-card">
              <div className="vi-ability-title">How to Use</div>
              <div className="vi-howto-steps">
                <p>Select or create a monster in the Monsters section.</p>
                <p>Review auto-filled scores (if applicable).</p>
                <p>Edit values to customise the encounter.</p>
                <p>Changes apply instantly {'\u2014'} no manual calculations required.</p>
              </div>
              <p className="vi-ability-tip">Use default values for balance, or adjust scores to scale difficulty.</p>
            </div>
          </div>
          <div className="ability-scores-container vi-ability-scores-wrap">
            <div className="vi-ability-scores">
              {ABILITIES.map(function(ab) {
                const score = abilityScores[ab];
                const mod = getAbilityModifier(score);
                const saveVal = (saves[ab] != null) ? saves[ab] : 0;
                return (
                  <div className="vi-ability-box" key={ab}>
                    <div className="vi-ability-label">{ab.toUpperCase()}</div>
                    <div className="vi-ability-inner">
                      <input
                        type="number"
                        id={ab + '-score-' + index}
                        value={score}
                        onChange={function(e) { updateAbilityScore(index, ab, e.target.value); }}
                        className="vi-ability-input"
                      />
                      <span id={ab + '-mod-' + index} className="vi-ability-mod">{'(' + signed(mod) + ')'}</span>
                      <span className="vi-ability-save">[Save: <span id={ab + '-save-display-' + index} className="vi-ability-mod-span">{signed(saveVal)}</span>]</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Stage 84 (Ability Check Runtime): the legacy "Ability Checks" card
              (battletracker.html 6464-6475) - six buttons, one per ability score,
              each rolling a d20 check via the CombatContext rollAbilityCheck
              runtime. Legacy invocation was rollAbilityCheck(index, ability) with
              no advantage/disadvantage (6469), so these call it as a normal roll.
              Smallest faithful equivalent: no menus, dropdowns, or dialogs. */}
          <div className="vi-ability-checks-card" id={'ability-checks-' + index}>
            <h4 className="vi-ability-checks-title">Ability Checks</h4>
            <div className="vi-ability-checks-grid">
              {ABILITIES.map(function(ab) {
                return (
                  <button
                    type="button"
                    key={ab}
                    className="vi-ability-check-btn"
                    onClick={function() { rollAbilityCheck(index, ab); }}
                  >
                    {ab.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Stage 88 (Saving Throw Runtime): the legacy "Saving Throws" card
              (battletracker.html 6477-6488) - six buttons, one per ability slot,
              each rolling a d20 SAVE via the CombatContext rollSave runtime. This
              is a SEPARATE card from Ability Checks (legacy had distinct cards and
              functions): a save uses the stored monster.saves[ability] bonus, not
              the score-derived modifier. Legacy invocation was rollSave(index,
              ability) with no advantage/disadvantage (6482), so these call it as a
              normal roll. Smallest faithful equivalent: no menus, dropdowns, or
              dialogs; not merged with Ability Checks. */}
          <div className="vi-saving-throws-card" id={'saving-throws-' + index}>
            <h4 className="vi-saving-throws-title">Saving Throws</h4>
            <div className="vi-saving-throws-grid">
              {ABILITIES.map(function(ab) {
                return (
                  <button
                    type="button"
                    key={ab}
                    className="vi-saving-throw-btn"
                    onClick={function() { rollSave(index, ab); }}
                  >
                    {ab.toUpperCase() + ' Save'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AbilityScoresView;
